/* global nexstar */

/**
 * File for logic corresponding to video center component, which is comprised
 * of a single video player, a carousel and a tabbed article list of video
 * thumbnails that are played in the one player.
 */

import setHistory from 'utils/setHistory';
import updateMeta from 'utils/updateMeta';
import createButton from 'utils/createButton';
import isMobile from 'utils/isMobile';
import clamp from 'utils/clamp';
import linkify from 'utils/linkify';

// eslint-disable-next-line max-len
import VideoPlaylistFactory from '../video-playlist-factory/video-playlist-factory';

export default class VideoCenter extends VideoPlaylistFactory {
  /**
   * Whether the Video Center has played its first video.
   *
   * @type {Boolean}
   */
  hasStartedPlaying;

  /**
   * Whether the video center player "sticky" position has been disabled.
   *
   * @type {Boolean}
   */
  hasStickyBeenDisabled = false;

  /**
   * Whether the video center displays uses a carousel as a playlist.
   *
   * @type {boolean}
   */
  isCarouselPlaylist = true;

  /**
   * The Slick carousel instance.
   *
   * @type {JQuery Element}
   */
  $slickInstance = null;

  /**
   * Create button to be used for exiting sticky video player
   *
   * @static
   * @memberof VideoCenter
   */
  static createExitStickyButton() {
    if (this.stickyExitButton) {
      return;
    }

    const { playlistWrapper } = this.children;
    const { mobileStickyClass, stickyExitButtonClass } = this.options;
    const { exit_sticky_player: labelText } = nexstar.template_variables.l10n;
    this.stickyExitButton = createButton(
      stickyExitButtonClass,
      '',
      [
        {
          name: 'aria-label',
          value: labelText,
        },
      ],
      () => {
        playlistWrapper.classList.remove(mobileStickyClass);
        this.stickyExitButton.remove();
        this.stickyExitButton = null;
        this.hasStickyBeenDisabled = true;
      },
    );
    playlistWrapper.appendChild(this.stickyExitButton);
  }

  /**
   * Update Open Graph URL when a URL is pushed to the browser history.
   *
   * @param {CustomEvent} event Event object.
   */
  static onSetHistory(event) {
    updateMeta('og:url', event.detail.url);
  }

  /**
   * Start the component
   */
  constructor(config) {
    // Start listening before constructing parent because it might push the first video URL to history.
    window.addEventListener('nsSetHistory', VideoCenter.onSetHistory);

    super(config);

    // Do nothing since we don't need to initialize slick anymore,
    // Use this file to load css only.
    return undefined;
  }

  /**
   * Handle OVP events.
   *
   * @param {CustomEvent} event Event object.
   */
  handleOVPEvents(event) {
    const { eventName, playerId } = event.detail || {};

    if ('playerUpdate' !== eventName) {
      super.handleOVPEvents(event);
      return;
    }

    const { playerWrapper } = this.children || {};

    if (! playerWrapper?.querySelector(`[id="${playerId}"]`)) {
      return;
    }

    const { args } = event.detail || {};

    this.setupContent({
      title: args.title,
      excerpt: args.description,
      date: {
        datetime: null,
        time: null,
      },
    });
  }

  /**
   * Stuff to do when clicking on a video thumbnail.
   *
   * @extends VideoPlaylistFactory.onVideoClick
   */
  onVideoClick(event) {
    const { playerWrapper } = this.children;
    const { index, collection: playlist } = event.currentTarget.dataset;

    // Maybe rebuild playlist.
    if (this.playlist !== playlist) {
      this.rebuildPlaylist(event);
    }

    // Set the playlist
    if (playlist) {
      super.setPlaylistOnPlayer(playlist);
    }

    // Set to new video
    if (index) {
      // We need index as an integer from here on.
      this.advanceToSlide(parseInt(index, 10), true);
    }

    // Scroll the entire video player into view with a little breathing room
    // above the player. Note that this does not account for the WP admin bar
    // for logged-in users.
    // TODO: abstract out / move to own module
    // eslint-disable-next-line max-len
    const playerYCoordinate = playerWrapper
      .getBoundingClientRect().top + window.pageYOffset;
    const playerYOffset = document.getElementById('masthead').offsetHeight;
    window.scrollTo({
      top: playerYCoordinate - playerYOffset - 16,
      behavior: 'smooth',
    });
  }

  /**
   * Move to a different "slide" in the playlist -- that is, a different video.
   *
   * @extends VideoPlaylistFactory.advanceToSlide
   * @param {Number} index Slide index.
   * @param {Boolean} isManuallyTriggered Indicates whether the slide change was triggered by a user action.
   */
  advanceToSlide(index, isManuallyTriggered) {
    const { playlistContent } = this.children;

    const slide = playlistContent.querySelector(
      `.article-list__article[data-index="${index}"]`,
    );

    if (slide) {
      try {
        // Find the video in the parent element matching the requested index and load the attached JSON.
        const params = JSON.parse(slide.dataset.anvatoParams);
        // If it's the first slide and autoplay is disabled, set is_manually_triggered to true.
        if (0 === index && ! params.autoplay) {
          params.is_manually_triggered = true;
        } else {
          params.is_manually_triggered = isManuallyTriggered;
        }
        this.setCurrentVideo(params, index);

        /**
         * Refresh all ads on the page when the video changes except when
         * starting the player when the page loads. A separate flag to track
         * whether this is the first video played is necessary because the
         * ad-loader entrypoint doesn't currently provide a way to specify when
         * ads should first load if not DOMCOntentLoaded. If it did (and if we
         * had a Video Center entry point) we would be able to call this event
         * for the first video to kick off ads.
         */
        if (this.hasStartedPlaying) {
          window.dispatchEvent(new CustomEvent('nsDfpRefreshSlots'));
        }

        this.hasStartedPlaying = true;
      } catch (e) {
        // Nothing.
      }
    }
  }

  /**
   * Set the player to a new video.
   *
   * @extends VideoPlaylistFactory.setCurrentVideo
   * @param {Object} videoJson  Anvato player configuration JSON.
   * @param {Number} videoIndex Index of video in the playlist.
   */
  setCurrentVideo(videoJson, videoIndex) {
    super.setCurrentVideo(videoJson, videoIndex);
    // Maybe update the Slick instance.
    // Don't trigger slick to "slide" to the very first slide. It is not necessary
    // and also it strangely causes `videoJson` not to be available on initial
    // page load.
    if (this.isCarouselPlaylist && 0 !== videoIndex) {
      this.$slickInstance.slick('slickGoTo', videoIndex);
    }

    if (! videoJson.ns) {
      return;
    }

    // Update article template and document metadata.
    this.setupContent(videoJson.ns);
  }

  /*
   * Setup title and metadata for the video player.
   */
  setupContent(data) {
    const {
      videoTitle, videoCategoryLink, videoCategoryText,
      videoTime, videoDescription,
    } = this.children;
    const {
      title, excerpt, category, thumbnail, date, link,
    } = data;

    if (videoTitle && title) {
      videoTitle.innerText = title;
    }

    if (! category) {
      videoCategoryLink.style.display = 'none';
    } else {
      if (videoCategoryLink && category.link) {
        videoCategoryLink.href = category.link;
      }
      if (videoCategoryText && (category?.text || category?.title)) {
        videoCategoryText.innerText = category?.text || category?.title;
      }
      videoCategoryLink.style.display = 'block';
    }

    if (! date.datetime || ! date.time) {
      videoTime.style.display = 'none';
    } else {
      videoTime.setAttribute('datetime', date.datetime);
      videoTime.innerText = date.time;
      videoTime.style.display = 'block';
    }

    if (videoDescription && excerpt) {
      videoDescription.innerHTML = linkify(excerpt); // phpcs:ignore WordPressVIPMinimum.JS.InnerHTML.Found
    }

    // Set history, document title and meta in browser.
    if (link) {
      // TODO: navigating back to a previous video?
      setHistory(link);
    }

    if (title) {
      document.title = title;
      updateMeta('og:title', title);
      updateMeta('twitter:title', title);
    }

    if (thumbnail) {
      updateMeta('og:image', thumbnail.src);
      updateMeta('twitter:image', thumbnail.src);
    }

    if (excerpt) {
      updateMeta('og:description', excerpt);
    }
  }

  /**
   * Set the video player size to always be visible within the viewport.
   *
   * This is an added fix to ensure that roughly the entire video player will be
   * visible on smaller screens such as laptops.
   *
   * A baseline approach for this is also done in CSS which is in reference to the
   * legacy codebase. However, the approach is not complete and the video player
   * is not confined to the browser viewport height.
   * See `@mixin above-the-fold-sizer`
   *
   * @param {object} event the event object.
   */
  setVideoPlayerToViewPort(event) {
    const { playerWrapper } = this.children;
    const { name, pInstance } = event.detail;

    if (
      ! playerWrapper
      || 'PLAYING_START' !== name
      || pInstance !== this.pInstance
    ) {
      return;
    }

    const offset = playerWrapper.getBoundingClientRect().top;
    // eslint-disable-next-line max-len
    playerWrapper.style.maxWidth = `calc( ( 16 * ( 100vh - ${offset}px ) ) / 9 )`;
  }

  /**
   * Toggle an open class for mobile collapsable content.
   */
  onHeaderToggle() {
    const { playlistWrapper } = this.children;
    const { openMobileCollapseClass } = this.options;
    playlistWrapper.classList.toggle(openMobileCollapseClass);

    if (this.isCarouselPlaylist) {
      // Recalculate slick to ensure it has the proper dimensions.
      this.$slickInstance.slick('setPosition');
    }
  }

  /**
   * Function to be run upon user page scroll.
   */
  onPageScroll() {
    if (! this.isCarouselPlaylist || ! isMobile()) {
      return;
    }

    const { playlistWrapper, videoTitle } = this.children;
    const player = playlistWrapper.querySelector('iframe[id^="player"]');

    if (! player) {
      return;
    }

    const {
      mobileStickyClass,
      siteHeaderHeight,
      openMobileCollapseClass,
    } = this.options;
    const playerRect = player.getBoundingClientRect();
    const playerBottomYCord = playerRect.bottom - playerRect.height / 2;

    // Player 'sticky' has not been previously disabled and is out-of-view.
    if (
      ! this.hasStickyBeenDisabled
      // Wait for bottom edge of player to align with the bottom edge of site
      // header. Account for site header height.
      && 0 > playerBottomYCord - siteHeaderHeight
    ) {
      playlistWrapper.classList.add(mobileStickyClass);
      playlistWrapper.classList.remove(openMobileCollapseClass); // collapse carousel filmstrip if open.
      this.createExitStickyButton();
      clamp(videoTitle, { clamp: 1 });
    }

    // We are back at the top.
    if (0 === window.scrollY) {
      playlistWrapper.classList.remove(mobileStickyClass);
      // '5' is a random high number of lines to 'remove' clamp since clamp
      // does not have that option.
      clamp(videoTitle, { clamp: 5 });
    }
  }

  /**
   * Rebuild the playlist that appears below the video player.
   *
   * @param {Object} event a click event.
   */
  rebuildPlaylist(event) {
    const { index: videoIndex } = event.currentTarget.dataset;

    // Get all the article html within the tab
    const playListArticles = $(event.currentTarget)
      .closest('.article-list__content')
      .html();

    if (this.isCarouselPlaylist) {
      // Destroy the current carousel
      this.$slickInstance.slick('unslick');
      // Clear out all the carousel articles
      this.$slickInstance.empty();
      // Append all the article html within the tab into the carousel
      this.$slickInstance.append(playListArticles);
      // Reinit the carousel
      this.$slickInstance
        .on('setPosition', this.setCarouselArrowPosition)
        .slick(this.options.slickOptions);
    } else {
      $(this.children.playlistContent).empty().append(playListArticles);
    }

    // Readd event listeners, labels etc.
    $.each(
      $(this.children.playlistContent).find('.article-list__article'),
      (index, article) => {
        article.addEventListener('click', this.onVideoClick);
      },
    );

    if (videoIndex) {
      const { upNextClass, playingClass } = this.options;
      const {
        up_next: upNextText,
        now_playing: nowPlayingText,
      } = window.nexstar.template_variables.l10n;
      super.setLabel(parseInt(videoIndex, 10) + 1, upNextClass, upNextText);
      super.setLabel(parseInt(videoIndex, 10), playingClass, nowPlayingText);
    }
  }
}
